import { Box, Button, Divider, Tooltip, Typography, useMediaQuery } from "@mui/material";
import { FC, useCallback, useEffect, useMemo } from "react";
import { FormDistribuidorRevenda } from "../FormDistribuidorRevenda";
import { BottomBar } from "src/framework/components/commons/BottomBar";
import { PageTitle } from "../PageTitle";
import { ValidateUtil } from "src/framework/commons/ValidateUtil";
import theme from "src/theme/light";
import { useProjetosHibridoStore } from "src/store/vendas/projetos/hibrido/hibrido.store";
import { FormProjetoHibridoType } from "src/store/vendas/projetos/hibrido/hibrido.types";
import { FormClienteHibrido } from "./FormClienteHibrido";
import { ModalEquipamentos } from "./ModalEquipamentos";
import { ModalPotencia } from "./ModalPotencia";
import { OrientacoesDimensionado } from "./OrientacoesDimensionado";

const tipoProjetoString: { [index: string]: { [index: string]: string } } = {
  "dimensionado": {
    "distribuicao_ongrid": "dimensionado Híbrido",
  },
}

interface FormDadosOffGridProperties {
    formState: FormProjetoHibridoType;
    onChangeValue: (formState: Partial<FormProjetoHibridoType>) => void
    errorState: string[];
    onChangeError: (errorState: string) => void
    distribuidorRevendaUsuario: any
    onClickNext: () => void
}

interface GetLatLngFromAddressType {
  cep: string;
  cidadeNome: string;
  estadoId: string;
}

export const FormDadosHibrido: FC<FormDadosOffGridProperties> = ({ onClickNext, formState, onChangeValue, errorState, onChangeError, distribuidorRevendaUsuario }) => {
  const { changeModalsValue } = useProjetosHibridoStore(store => store.actions)

  const projetosHibridoValidationsStoreState = useProjetosHibridoStore(store => store.state.validations)
  const projetosHibridoLoadingsStoreState = useProjetosHibridoStore(store => store.state.loadings)
  const projetosHibridoModalsStoreState = useProjetosHibridoStore(store => store.state.modals)
  const projetosHibridoModalsStoreActions = useProjetosHibridoStore(store => store.actions)
  const projetosHibridoStoreState = useProjetosHibridoStore(store => store.state.data)
  
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const FooterBottom = isMobile ? Box : BottomBar;

  const nextButtonIsDisabled = useMemo(() => {
    const validationsStoreFields: (keyof typeof projetosHibridoValidationsStoreState)[] = ["modulo", 'tipo_bateria']
    const loadingFields: (keyof typeof projetosHibridoLoadingsStoreState)[] = ['listagemEstruturas', 'calculoModulos', 'listagemModulos']

    const validationStoreIsInvalid = validationsStoreFields.some(item => {
      return !projetosHibridoValidationsStoreState[item].status
    })

    if (validationStoreIsInvalid) return true;

    // Desabilita o botão "Continuar", caso algum loading da lista **loadingFields** esteja ativo...
    if (loadingFields.some(loadingKey => projetosHibridoLoadingsStoreState[loadingKey])) return true;

    const requiredFields: (keyof FormProjetoHibridoType)[] = [
      "cliente_proposta",
      "cidade",
      "estado",
      'tensao_saida',
      'modulo_selecionado',
      'fileiras',
      "latitude",
      "longitude",
      'tipo_bateria',
      'potencia_desejada_w',
      'potencia_desejada_kw',
      'baterias',
    ]

    if (!!formState.tipo_bateria?.id && !formState.taxa_descarga) {
      requiredFields.push('taxa_descarga')
    }

    if (!!formState.tipo_bateria?.id && (!formState.total_carga_equipamentos_kw || !formState.total_carga_equipamentos_w)) {
      requiredFields.push('total_carga_equipamentos_kw', 'total_carga_equipamentos_w')
    }
    
    for (const requiredField of requiredFields) {
      if (!formState?.[requiredField]) {
        return true;
      }
    }

    if (formState.fileiras.length && (!formState.fileiras[0].tipo_estrutura || !formState.fileiras[0].posicao_modulo)) return true;

    if (formState?.email_proposta && (!ValidateUtil.isValidEmail(formState?.email_proposta) || errorState?.includes('email_proposta'))) return true;

  }, [projetosHibridoLoadingsStoreState, projetosHibridoValidationsStoreState, formState, errorState])

  const pageTitle = useMemo(()=>{
    if (!formState?.tipo || !formState?.segmento) return '';
    const tipoString = tipoProjetoString[formState.tipo][formState.segmento] || formState.tipo;
    
    if (!formState?.clonar_projeto && formState?.isEdit) return `Edição de projeto ${tipoString}`
    return `Novo projeto ${tipoString}`
  },[formState?.clonar_projeto, formState?.tipo, formState?.isEdit, formState.segmento])

  return (
    <>
      <Box p={2}>
        <PageTitle title={pageTitle} subtitle='Preencha os dados do cliente' />
      </Box>

      <Divider className="X12" />

      <Box p={2}>
        <Box className="X12" py={2}>
          <Typography variant="h6" align="left"><strong>Dados do cliente:</strong></Typography>
        </Box>

        <FormDistribuidorRevenda
          errorState={errorState}
          formState={formState}
          distribuidorRevendaUsuario={distribuidorRevendaUsuario}
          onChangeError={onChangeError}
          onChangeValue={onChangeValue}
        />

        <FormClienteHibrido
          errorState={errorState}
          onChangeError={onChangeError}
          formState={formState}
          onChangeValue={onChangeValue}
        />
      </Box>

      <OrientacoesDimensionado />

      <FooterBottom className="fotter-bottom">
        <Tooltip title={nextButtonIsDisabled ? 'Para prosseguir, preencha os campos marcados como *obrigatório.' : ''}>
          <span>
            <Button
              disableElevation={true}
              disabled={nextButtonIsDisabled}
              color={"primary"}
              variant={"contained"}
              onClick={onClickNext}
              fullWidth={isMobile}
              >
              Continuar meu projeto
            </Button>
          </span>
        </Tooltip>

        { nextButtonIsDisabled && isMobile && <Box className="label-inputs" color="">
          <small>Para prosseguir, preencha os campos marcados como <span className="label-emphasis">*</span>obrigatório.</small>
        </Box>}
      </FooterBottom>

        {projetosHibridoModalsStoreState.equipamentos && (
          <ModalEquipamentos
            open={projetosHibridoModalsStoreState.equipamentos}
            onClose={() => changeModalsValue({ equipamentos: false })}
            initialData={formState?.cargas_prioritarias || []}
            onChangeValue={(equipamentos) => onChangeValue(equipamentos)}
          />
        )}

        {projetosHibridoModalsStoreState.potencia_desejada && (
          <ModalPotencia
            open={projetosHibridoModalsStoreState.potencia_desejada}
            onClose={() => changeModalsValue({ potencia_desejada: false })}
          />
        )}
    </>
  )
}
